/**
 * @file fileDrop.ts
 * Shared drag-and-drop utilities for file drop zones.
 *
 * Exports:
 *   DROPPED_FILES_EVENT     window event name dispatched by viewport-level drop zones
 *   APP_DROPPED_FILES_EVENT window event name dispatched by app-level drop (file-droppable)
 *   DroppedFileInfo         shape of a single dropped file
 *   isFileDragEvent         guard: does the DragEvent carry files?
 *   extractDroppedFiles     read DroppedFileInfo[] from a DragEvent
 *   DropOverlay             visual overlay injected into a shadow root
 *   DragDropHandler         attaches/removes the four drag event listeners
 *
 * Precedence contract
 * -------------------
 * Child drop zones (as-viewport, as-file-upload) call e.stopPropagation() on
 * the native drop event inside their onDrop callback.  Parent zones listening
 * in bubble phase (as-app file-droppable) therefore never see drops that a
 * child already handled.
 */

import { AsFileHandle } from './as-file-handle.ts';

export const DROPPED_FILES_EVENT     = 'as-dropped-files';
export const APP_DROPPED_FILES_EVENT = 'as-app-dropped-files';

export const IMPORT_ARROW_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" style="transform:scaleY(-1)"><path d="M224 30v256h-64l96 128 96-128h-64V30h-64zM32 434v48h448v-48H32z" fill="#000000" fill-opacity="1"/></svg>`;
export const TICK_SVG         = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M17.47 250.9C88.82 328.1 158 397.6 224.5 485.5c72.3-143.8 146.3-288.1 268.4-444.37L460 26.06C356.9 135.4 276.8 238.9 207.2 361.9c-48.4-43.6-126.62-105.3-174.38-137z" fill="#000000" fill-opacity="1"/></svg>`;

// ---- Types ----

/**
 * @deprecated Drop events now carry `AsFileHandle[]` (unified with
 * <as-file-upload>'s `as-file-selected`). This alias keeps old type imports
 * compiling.
 * TODO(file-api-cleanup): remove `DroppedFileInfo` once consumers import
 * `AsFileHandle` directly.
 */
export type DroppedFileInfo = AsFileHandle;

// ---- Utilities ----

export function isFileDragEvent(e: DragEvent): boolean {
  return !!e.dataTransfer?.types?.includes('Files');
}

/** Wrap each dropped native File in an AsFileHandle — the same value type
 *  <as-file-upload> emits, so drop and pick consumers read one shape. */
export function extractDroppedFiles(e: DragEvent): AsFileHandle[] {
  const files = e.dataTransfer?.files;
  if (!files?.length) return [];
  return Array.from(files).map(f => new AsFileHandle(f));
}

export let dropOverlaySpinner = false;

// ---- DropOverlay ----

/**
 * Manages a purely visual drop-overlay element inside a shadow root.
 * The host's stylesheet must define styles for .drop-overlay and .drop-overlay__icon.
 */
export class DropOverlay {
  private _el:   HTMLElement;
  private _icon: HTMLElement;
  private _postDrop = false;

  constructor(shadowRoot: ShadowRoot) {
    this._el = document.createElement('div');
    this._el.className = 'drop-overlay';
    this._icon = document.createElement('div');
    this._icon.className = 'drop-overlay__icon';
    this._icon.innerHTML = IMPORT_ARROW_SVG;
    this._el.appendChild(this._icon);
    shadowRoot.appendChild(this._el);
  }

  /** True while the post-drop animation is running — suppresses new enter/leave reactions. */
  get postDrop(): boolean { return this._postDrop; }

  show(): void { this._el.classList.add('active'); }
  hide(): void { this._el.classList.remove('active'); }

  setIcon(state: 'arrow' | 'spinner' | 'tick'): void {
    switch (state) {
      case 'arrow':   this._icon.innerHTML = IMPORT_ARROW_SVG; break;
      case 'spinner': this._icon.innerHTML = '<as-spinner></as-spinner>'; break;
      case 'tick':    this._icon.innerHTML = TICK_SVG; break;
    }
  }

  /** Remove the overlay element from the shadow root (call when disabling the drop zone). */
  remove(): void {
    this._el.remove();
  }

  /** Runs the spinner → tick → hide sequence after a successful drop. */
  animateDrop(): void {
    this._postDrop = true;
    if (dropOverlaySpinner) {
      this.setIcon('spinner');
      setTimeout(() => {
        this.setIcon('tick');
        setTimeout(() => {
          this._postDrop = false;
          this.hide();
          this.setIcon('arrow');
        }, 800);
      }, 500);
    } else {
      this.setIcon('tick');
      setTimeout(() => {
        this._postDrop = false;
        this.hide();
        this.setIcon('arrow');
      }, 800);
    }
  }
}

// ---- DragDropHandler ----

/**
 * Attaches dragenter/dragleave/dragover/drop listeners to a host element,
 * optionally driving a DropOverlay for visual feedback.
 *
 * @param host     Element to attach listeners to.
 * @param onDrop   Called with the extracted files and the original DragEvent.
 *                 Call e.stopPropagation() inside to prevent parent drop zones
 *                 (e.g. as-app file-droppable) from also handling the same drop.
 * @param overlay  Optional DropOverlay to show/animate. Pass null for zones
 *                 that need no visual feedback (e.g. app-level fallback handler).
 * @param capture  Whether to attach in capture phase (default true).
 *                 Use true for child zones so they intercept before parents.
 *                 Use false for app-level fallback listeners (bubble phase).
 */
export class DragDropHandler {
  private _host:     HTMLElement;
  private _overlay:  DropOverlay | null;
  private _onDropCb: (files: AsFileHandle[], e: DragEvent) => void;
  private _capture:  boolean;

  constructor(
    host: HTMLElement,
    onDrop: (files: AsFileHandle[], e: DragEvent) => void,
    overlay: DropOverlay | null = null,
    capture = true,
  ) {
    this._host     = host;
    this._onDropCb = onDrop;
    this._overlay  = overlay;
    this._capture  = capture;

    host.addEventListener('dragenter', this._onDragEnter, capture);
    host.addEventListener('dragleave', this._onDragLeave, capture);
    host.addEventListener('dragover',  this._onDragOver,  capture);
    host.addEventListener('drop',      this._onDrop,      capture);
  }

  private _onDragEnter = (e: DragEvent): void => {
    if (this._overlay?.postDrop) return;
    if (!isFileDragEvent(e)) return;
    this._overlay?.show();
  };

  private _onDragLeave = (e: DragEvent): void => {
    if (this._overlay?.postDrop) return;
    const related = e.relatedTarget as Node | null;
    const host    = this._host;
    if (!related || (!host.contains(related) && !host.shadowRoot?.contains(related))) {
      this._overlay?.hide();
    }
  };

  private _onDragOver = (e: DragEvent): void => {
    if (!isFileDragEvent(e)) return;
    e.preventDefault();
  };

  private _onDrop = (e: DragEvent): void => {
    e.preventDefault();
    const files = extractDroppedFiles(e);
    if (!files.length) {
      this._overlay?.hide();
      return;
    }
    this._overlay?.animateDrop();
    this._onDropCb(files, e);
  };

  destroy(): void {
    this._host.removeEventListener('dragenter', this._onDragEnter, this._capture);
    this._host.removeEventListener('dragleave', this._onDragLeave, this._capture);
    this._host.removeEventListener('dragover',  this._onDragOver,  this._capture);
    this._host.removeEventListener('drop',      this._onDrop,      this._capture);
  }
}
